import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLORS, ROUTE_PATHS } from '@constants';
import { CAMPAIGN_TYPE_ID } from '@enums';
import { useStore } from '@hooks';
import { Icon, Loader, Typography } from '@ui';
import { hasNonDemoProducts } from '@utils/filterProducts';
import { Flex, notification } from 'antd';
import { observer } from 'mobx-react';
import { usePreWizardContext } from '../../PreWizardContext';
import {
    Container,
    OptionCard,
    OptionContent,
    OptionDescription,
    OptionNote,
    OptionRow,
    OptionsGrid,
    OptionTitle
} from './styled';

const PreWizardLanding = observer(() => {
    const navigate = useNavigate();
    const store = useStore();
    const { isManualStoreWithNoCampaigns } = store.accountsStore;
    const { createCampaign } = store.campaignWizardStore;
    const { createDemoProducts, getProducts } = store.productsStore;
    const { selectedOption, setSelectedOption, setIsButtonEnabled, setOnButtonClick } = usePreWizardContext();
    const [isDemoLoading, setIsDemoLoading] = useState(false);

    const memoizedGetProducts = useCallback(() => {
        return getProducts();
    }, [getProducts]);

    useEffect(() => {
        if (!isManualStoreWithNoCampaigns) {
            return;
        }

        const checkHasProducts = async () => {
            await memoizedGetProducts();
            const { products, meta } = store.productsStore;

            if (hasNonDemoProducts(products, meta)) {
                navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_1, { replace: true });
            }
        };

        checkHasProducts();
    }, [isManualStoreWithNoCampaigns, memoizedGetProducts, navigate, store.productsStore]);

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
    };

    const handleCreateCampaignDemo = useCallback(async () => {
        setIsDemoLoading(true);
        try {
            await createDemoProducts();

            const campaign = await createCampaign({
                title: 'My Entire Catalog',
                catalogType: CAMPAIGN_TYPE_ID.MY_ENTIRE_CATALOG
            });
            if (campaign?.id) {
                navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_STEP_2.replace(':id', campaign.id));
            }
        } catch (err) {
            notification.error({
                message: 'Something went wrong'
            });
        } finally {
            setIsDemoLoading(false);
        }
    }, [createDemoProducts, createCampaign, navigate]);

    const handleConnectProducts = useCallback(async () => {
        if (!selectedOption) return;

        if (selectedOption === 'product-feed') {
            navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_FEED);
        } else if (selectedOption === 'api') {
            navigate(ROUTE_PATHS.CAMPAIGNS_CREATE_MANUAL_API);
        } else if (selectedOption === 'demo') {
            await handleCreateCampaignDemo();
        }
    }, [selectedOption, navigate, handleCreateCampaignDemo]);

    useEffect(() => {
        setIsButtonEnabled(!!selectedOption && !isDemoLoading);
    }, [selectedOption, isDemoLoading, setIsButtonEnabled]);

    useEffect(() => {
        setOnButtonClick(() => handleConnectProducts);
    }, [setOnButtonClick, handleConnectProducts]);

    return (
        <Flex vertical style={{ flexGrow: 1 }}>
            <Container>
                <Typography.Title
                    data-e2e='onboarding-wizard_title'
                    level={1}
                    color={COLORS.$gray100Black}
                    style={{ marginBottom: '16px' }}
                >
                    Select how to sync your catalog
                </Typography.Title>

                <Typography.Text
                    style={{ marginBottom: '24px', display: 'block' }}
                    color={COLORS.$gray60Black}
                    type='body2'
                    data-e2e='onboarding-wizard_description'
                >
                    To create campaigns on Klickly, first connect your product catalog. Choose the sync method that best
                    fits your store setup.
                </Typography.Text>

                <OptionsGrid>
                    <OptionRow>
                        <OptionCard
                            $isSelected={selectedOption === 'product-feed'}
                            onClick={() => handleOptionSelect('product-feed')}
                            data-e2e='onboarding-wizard_connect-via-product-feed_card'
                        >
                            <Icon
                                color={COLORS.$gray60Black}
                                size='80px'
                                strokeWidth={2}
                                component={() => <Icon.AddMoreProductsIcon />}
                            />
                            <OptionContent>
                                <OptionTitle>Connect via product feed</OptionTitle>
                                <OptionDescription>
                                    Import your catalog using an existing feed from your e-commerce platform
                                </OptionDescription>
                            </OptionContent>
                        </OptionCard>

                        <OptionCard
                            $isSelected={selectedOption === 'api'}
                            onClick={() => handleOptionSelect('api')}
                            data-e2e='onboarding-wizard_connect-via-api_card'
                        >
                            <Icon size='80px' component={() => <Icon.KlicklyLogo />} />
                            <OptionContent>
                                <OptionTitle>Connect via API</OptionTitle>
                                <OptionDescription>
                                    Sync your catalog directly through API integration for the most stable data flow
                                </OptionDescription>
                            </OptionContent>
                        </OptionCard>
                    </OptionRow>

                    <OptionRow>
                        <OptionCard
                            $isSelected={selectedOption === 'demo'}
                            onClick={() => !isDemoLoading && handleOptionSelect('demo')}
                            style={{
                                position: 'relative',
                                opacity: isDemoLoading ? 0.6 : 1,
                                pointerEvents: isDemoLoading ? 'none' : 'auto'
                            }}
                            data-e2e='onboarding-wizard_proceed-with-demo-catalog_card'
                        >
                            {isDemoLoading && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 10
                                    }}
                                >
                                    <Loader />
                                </div>
                            )}
                            <Icon
                                color={COLORS.$gray60Black}
                                size='80px'
                                strokeWidth={2}
                                component={() => <Icon.ProductNoImageIcon />}
                            />
                            <OptionContent>
                                <OptionTitle>Proceed with DEMO catalog</OptionTitle>
                                <OptionDescription>Start quickly with dummy products.</OptionDescription>
                            </OptionContent>
                            <OptionNote>
                                You&apos;ll still have to complete product set-up after campaign is created
                            </OptionNote>
                        </OptionCard>
                    </OptionRow>
                </OptionsGrid>
            </Container>
        </Flex>
    );
});

export default PreWizardLanding;
